import { QuestContractReadFunctionNames } from '../constants/types';
import { PublicClient, type Abi } from 'viem';

import {
  defaultsForChain,
  isRetryableError,
  ReadRetryOptions,
  sleep,
} from 'shared/utils/retryUtils';

import QUEST_ABI from './quest-abi.json';

const QUEST_CONTRACT_ADDRESS = import.meta.env.VITE_QUEST_CONTRACT as `0x${string}`;

export const readContractCallWithRetry = async <T>(
  provider: PublicClient,
  address: `0x${string}`,
  abi: Abi | readonly unknown[],
  functionName: string,
  args: Parameters<PublicClient['readContract']>[0]['args'] = [],
  options: ReadRetryOptions<T> = {},
): Promise<T> => {
  const base = defaultsForChain(provider);
  const attempts = options.attempts ?? base.attempts;
  const backoff = options.backoff ?? base.backoff;
  const jitterMs = options.jitterMs ?? base.jitterMs;
  const initialDelay = options.delayMs ?? base.delayMs;

  const clients: PublicClient[] = [provider, ...(options.fallbacks ?? [])];

  let lastErr: unknown;

  for (const client of clients) {
    let delay = initialDelay;

    for (let i = 0; i < attempts; i++) {
      try {
        const result = (await client.readContract({
          address,
          abi,
          functionName,
          args,
          authorizationList: undefined,
        })) as T;

        if (options.validate && !options.validate(result)) {
          // treat as retryable "empty/stale" read
          throw new Error('ValidationFailed');
        }

        return result;
      } catch (error) {
        lastErr = error;

        // Non-retryable? bubble up immediately.
        if (!isRetryableError(error) && (error as any)?.message !== 'ValidationFailed') {
          break;
        }

        // If this was our last try on this client, break to next client.
        const lastTryOnThisClient = i === attempts - 1;
        if (lastTryOnThisClient) break;

        // sleep with jitter, then back off
        const jitter = Math.floor((Math.random() - 0.5) * 2 * jitterMs);
        await sleep(Math.max(0, delay + jitter));
        delay = Math.floor(delay * backoff);
      }
    }
  }

  // include the original function name and last error message for easier telemetry
  const msg = (lastErr as any)?.message ? `: ${(lastErr as any).message}` : '';
  throw new Error(`Failed to read ${functionName}${msg}`);
};

export const readGetUserInfo = (provider: PublicClient, account: `0x${string}`) =>
  readContractCallWithRetry<bigint>(
    provider,
    QUEST_CONTRACT_ADDRESS,
    QUEST_ABI as Abi,
    QuestContractReadFunctionNames.GET_USER_INFO,
    [account],
  );
